
import React, { useState } from 'react';
import { SimulationConfig, BallSoundEffect, BallCollisionSoundType } from '@/types';
import { AUDIO } from '@/services/AudioService';
import { 
  Music, 
  Dices, 
  Upload, 
  Trash2, 
  Edit2, 
  Play, 
  Cloud, 
  Box, 
  Activity,
  Layers
} from 'lucide-react';

import { InfoPopup } from './InfoPopup';

interface CustomSoundUploadSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  onBallSoundUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingBallSound: boolean;
}

export const CustomSoundUploadSection: React.FC<CustomSoundUploadSectionProps> = ({ config, setConfig, onBallSoundUpload, isUploadingBallSound }) => {
  const [activeSubTab, setActiveSubTab] = useState<'COLLISION' | 'BREAK'>('BREAK');
  
  const soundEffects: BallSoundEffect[] = [
    'Pop', 'Wood', 'Metal', 'Synth-Zap', 'Crystal', 'Soft-Thud', 'Explosion',
    'Laser-Gun', 'Pistol', 'Sniper', 'Glass-Break', 'Digital-Beep', 
    'Retro-Jump', 'Thunder', 'Bubble', 'Camera', 'Sword', 
    'Robot', 'Alien', 'Bass-Drop', 'Cowbell', 'Snare', 
    'Kick', 'Hi-Hat', 'Cyber-Pulse', 'Magic', 'Sparkle', 
    'Steam', 'Electric', 'Glitch', 'Heavy-Slam', 
    'Echo-Pluck', 'Sub-Boom', 'Whip', 'SciFi-Door', 'Wood-Block', 'Marimba'
  ];

  const removeUserSound = (id: string) => {
    const updated = config.userSounds.filter(s => s.id !== id);
    setConfig(prev => ({
        ...prev,
        userSounds: updated,
        selectedUserSoundId: prev.selectedUserSoundId === id ? undefined : prev.selectedUserSoundId,
        selectedBreakUserSoundId: prev.selectedBreakUserSoundId === id ? undefined : prev.selectedBreakUserSoundId
    }));
  };

  const renameUserSound = (id: string, currentName: string) => {
    const newName = prompt("Enter new name for sound effect:", currentName);
    if (newName === null) return;
    const finalName = newName.trim() || currentName;
    setConfig(prev => ({
      ...prev,
      userSounds: prev.userSounds.map(s => s.id === id ? { ...s, name: finalName } : s)
    }));
  };

  const handleSetSoundType = (type: BallCollisionSoundType) => {
    const key = activeSubTab === 'COLLISION' ? 'ballCollisionSoundType' : 'breakSoundType';
    setConfig(prev => ({ ...prev, [key]: type }));
  };

  const handleSelectSound = (eff: BallSoundEffect) => {
    const keyType = activeSubTab === 'COLLISION' ? 'ballCollisionSoundType' : 'breakSoundType';
    const keyEff = activeSubTab === 'COLLISION' ? 'selectedBallSoundEffect' : 'selectedBreakSoundEffect';
    setConfig(prev => ({ 
        ...prev, 
        [keyType]: 'SOUND_EFFECT',
        [keyEff]: eff
    }));
    AUDIO.playSoundEffect(eff, config, 0.5);
  };

  const handleSelectUserSound = (id: string) => {
    const keyType = activeSubTab === 'COLLISION' ? 'ballCollisionSoundType' : 'breakSoundType';
    const keyId = activeSubTab === 'COLLISION' ? 'selectedUserSoundId' : 'selectedBreakUserSoundId';
    const sound = config.userSounds.find(s => s.id === id);
    setConfig(prev => ({ 
        ...prev, 
        [keyType]: 'CUSTOM',
        [keyId]: id
    }));
    if (sound) {
        const source = sound.url || sound.base64;
        if (source) AUDIO.playCustomBuffer(source, 0.5, config);
    }
  };

  const currentType = activeSubTab === 'COLLISION' ? config.ballCollisionSoundType : config.breakSoundType;
  const currentEff = activeSubTab === 'COLLISION' ? config.selectedBallSoundEffect : config.selectedBreakSoundEffect;
  const currentUserSoundId = activeSubTab === 'COLLISION' ? config.selectedUserSoundId : config.selectedBreakUserSoundId;

  return (
    <div className={`p-8 rounded-[2.5rem] border shadow-sm transition-colors duration-300 ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-zinc-50 border-zinc-950/5'}`}>
      <div className="flex items-center gap-3 mb-8 px-1">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${config.darkMode ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/10 shadow-[0_0_15px_rgba(6,182,212,0.15)]' : 'bg-zinc-50 text-cyan-600 border border-zinc-100 shadow-sm'}`}>
          <Activity className="w-5 h-5" />
        </div>
        <div className="flex items-center gap-2">
          <h3 className="text-[12px] font-medium uppercase tracking-[0.3em] opacity-60">Sample Trigger Node</h3>
          <InfoPopup settingKey="sampleTriggerNode" darkMode={config.darkMode} />
        </div>
      </div>

      <div className="flex flex-col gap-8">
        {/* Toggle Target */}
        <div className="flex flex-col gap-3">
          <span className="text-[9px] font-medium uppercase tracking-[0.2em] opacity-30 ml-1">Relay Routing</span>
          <div className={`grid grid-cols-2 gap-2 p-1.5 rounded-[1.5rem] border ${config.darkMode ? 'bg-black/30 border-white/5 shadow-inner' : 'bg-white border-zinc-100 shadow-inner'}`}>
            <button onClick={() => setActiveSubTab('COLLISION')} className={`py-2.5 rounded-xl text-[9px] font-medium uppercase tracking-widest transition-all ${activeSubTab === 'COLLISION' ? (config.darkMode ? 'bg-white text-zinc-950 border-white' : 'bg-white text-cyan-600 border-cyan-600/30 shadow-md') : 'text-zinc-400 hover:text-zinc-600'}`}>Bounces</button>
            <button onClick={() => setActiveSubTab('BREAK')} className={`py-2.5 rounded-xl text-[9px] font-medium uppercase tracking-widest transition-all ${activeSubTab === 'BREAK' ? (config.darkMode ? 'bg-white text-zinc-950 border-white' : 'bg-white text-cyan-600 border-cyan-600/30 shadow-md') : 'text-zinc-400 hover:text-zinc-600'}`}>Breaks</button>
          </div>
        </div>

        {/* Source Matrix */}
        <div className="flex flex-col gap-3">
          <span className="text-[9px] font-medium uppercase tracking-[0.2em] opacity-30 ml-1">Stream Source</span>
          <div className={`grid grid-cols-3 gap-2 p-1.5 rounded-[1.2rem] border ${config.darkMode ? 'bg-black/30 border-white/5 shadow-inner' : 'bg-white border-zinc-100 shadow-inner'}`}>
            <button onClick={() => handleSetSoundType('MELODY')} className={`py-2 rounded-lg text-[8px] font-medium uppercase tracking-widest transition-all ${currentType === 'MELODY' ? (config.darkMode ? 'bg-white border-white text-zinc-950' : 'bg-white border-cyan-600/30 text-cyan-600 shadow-sm') : 'text-zinc-400'}`}>Melody</button>
            <button onClick={() => handleSetSoundType('SOUND_EFFECT')} className={`py-2 rounded-lg text-[8px] font-medium uppercase tracking-widest transition-all ${currentType === 'SOUND_EFFECT' ? (config.darkMode ? 'bg-white border-white text-zinc-950' : 'bg-white border-cyan-600/30 text-cyan-600 shadow-sm') : 'text-zinc-400'}`}>FX Hub</button>
            <button onClick={() => handleSetSoundType('CUSTOM')} className={`py-2 rounded-lg text-[8px] font-medium uppercase tracking-widest transition-all ${currentType === 'CUSTOM' ? (config.darkMode ? 'bg-white border-white text-zinc-950' : 'bg-white border-cyan-600/30 text-cyan-600 shadow-sm') : 'text-zinc-400'}`}>Cloud</button>
          </div>
        </div>

        {/* Content Area */}
        <div className={`p-6 rounded-[2rem] border min-h-[120px] flex items-center justify-center ${config.darkMode ? 'bg-white/5 border-white/5' : 'bg-white border-zinc-950/5 shadow-sm'}`}>
          {currentType === 'SOUND_EFFECT' && (
            <div className="flex flex-col gap-4 w-full">
              <div className="flex items-center gap-2 opacity-30 ml-1">
                <Dices className="w-3.5 h-3.5" />
                <span className="text-[9px] font-display font-black uppercase tracking-widest">Library Access</span>
              </div>
              <select 
                  value={currentEff} 
                  onChange={e => handleSelectSound(e.target.value as BallSoundEffect)}
                  className={`w-full h-12 px-5 rounded-2xl text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer border ${config.darkMode ? 'bg-black/30 border-white/5 text-white' : 'bg-white border-zinc-950/5 text-zinc-950 shadow-inner'}`}
              >
                  {soundEffects.map(s => <option key={s} value={s}>{s.replace('-', ' ')}</option>)}
              </select>
            </div>
          )}

          {currentType === 'CUSTOM' && (
            <div className="flex flex-col gap-6 w-full">
                <label className={`w-full h-14 rounded-2xl border-2 border-dashed text-[9px] font-display font-black uppercase tracking-widest cursor-pointer flex items-center justify-center gap-3 transition-all ${config.darkMode ? 'border-white/10 hover:bg-white/5 text-white/40' : 'border-zinc-950/10 bg-white hover:bg-zinc-50 text-zinc-400 shadow-sm'}`}>
                    {isUploadingBallSound ? <Activity className="w-4 h-4 animate-pulse" /> : <Upload className="w-4 h-4" />}
                    {isUploadingBallSound ? 'Transmitting...' : 'Upload Asset'}
                    <input type="file" accept="audio/*" onChange={onBallSoundUpload} className="hidden" />
                </label>
                
                <div className="flex flex-col gap-2 max-h-60 overflow-y-auto pr-2">
                    {config.userSounds.length === 0 ? (
                        <div className="text-center py-10 opacity-20 flex flex-col items-center gap-3">
                          <Box className="w-6 h-6" />
                          <span className="text-[10px] font-black uppercase tracking-widest">Buffer Empty</span>
                        </div>
                    ) : (
                      config.userSounds.map(s => (
                        <div key={s.id} className={`group flex items-center justify-between p-4 rounded-2xl border transition-all ${currentUserSoundId === s.id ? (config.darkMode ? 'bg-white border-white text-zinc-950 rounded-xl' : 'bg-white border-cyan-600/30 text-cyan-600 shadow-md') : (config.darkMode ? 'bg-white/5 border-white/5' : 'bg-zinc-50 border-zinc-100')}`}>
                            <div className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer" onClick={() => handleSelectUserSound(s.id)}>
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${currentUserSoundId === s.id ? (config.darkMode ? 'bg-zinc-950/10 text-zinc-950' : 'bg-cyan-50 text-cyan-600') : 'bg-white text-zinc-300 shadow-sm border border-zinc-100'}`}>
                                  {s.url ? <Cloud className="w-4 h-4" /> : <Layers className="w-4 h-4" />}
                                </div>
                                <div className="flex flex-col truncate">
                                    <span className="text-[10px] font-black uppercase tracking-widest truncate">{s.name}</span>
                                    <span className="text-[8px] opacity-40 uppercase font-bold tracking-widest">{currentUserSoundId === s.id ? 'Signal Active' : 'Select Source'}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => renameUserSound(s.id, s.name)} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${currentUserSoundId === s.id ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-white text-zinc-400 hover:text-zinc-600 border border-zinc-950/5'}`}><Edit2 className="w-3 h-3" /></button>
                                <button onClick={() => {
                                    const src = s.url || s.base64;
                                    if(src) AUDIO.playCustomBuffer(src, 0.5, config);
                                }} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${currentUserSoundId === s.id ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-white text-zinc-400 hover:text-zinc-600 border border-zinc-950/5'}`}><Play className="w-3 h-3 fill-current" /></button>
                                <button onClick={() => removeUserSound(s.id)} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${currentUserSoundId === s.id ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-rose-50 text-rose-500 hover:bg-rose-100 border border-rose-200'}`}><Trash2 className="w-3 h-3" /></button>
                            </div>
                        </div>
                      ))
                    )}
                </div>
            </div>
          )}
          
          {currentType === 'MELODY' && (
            <div className="flex items-center gap-4 py-8">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20">
                <Music className="w-6 h-6 text-indigo-500" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-500">Global Stream Active</span>
                <span className="text-[9px] font-bold uppercase opacity-40">{config.selectedMelody}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
